import React, { useEffect, useMemo, useState } from "react";
import TableDesign, {
  TableHeading,
} from "../../../components/common/table/TableDesign";
import {
  Box,
  TableCell,
  TableRow,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import useStyles from "../../../components/pageTitle/styles";
import CustomButton from "../../../components/common/button/CustomButton";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  getAllComplaintsAction,
  setAddComplaintResponse,
} from "../../../store/reducers/complaintSlice";
import dayjs from "dayjs";
import PageTitle from "../../../components/pageTitle/PageTitle";
import ManageComplaint from "../complaints/ManageComplaint";

// Stage options for filtering
const COMPLAINT_STAGES = [
  "Initiated",
  "Assigned",
  "Data acquiring",
  "Evaluated",
  "Insurance claim",
  "Recovery initiated",
  "Other parties claims",
  "Accept / reject",
  "Settled",
  "Closed",
  "Archived",
  "Rejected",
  "All",
];

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  status: string;
  leadId?: string;
}

const ComplaintList = ({ show,activeQuoteId }: { show: boolean,activeQuoteId?:any }) => {
  const classes = useStyles();
  const { complaints, isAddComplaintSuccess } = useSelector(
    (state: any) => state?.complaint,
  );
  const { leadDetails } = useSelector((state: any) => state.lead);
  const { userProfile } = useSelector((state: any) => state?.user);
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<any>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [filterOption, setFilterOption] = React.useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "",
    orderIn: "asc",
    status: "",
    leadId: leadDetails?.lead?.generated_id || "",
  });

  useEffect(() => {
    if (filterOption.leadId) {
      dispatch(getAllComplaintsAction(filterOption));
    }
  }, [filterOption, leadDetails?.lead?.generated_id]);

  useEffect(() => {
    if (isAddComplaintSuccess) {
      dispatch(getAllComplaintsAction(filterOption));
      dispatch(setAddComplaintResponse(false));
      setShowModal(false);
    }
  }, [isAddComplaintSuccess]);

  // Filter complaints based on status
const filteredComplaints = useMemo(() => {
  return complaints?.filter((item: any) => {
    const statusMatch =
      filterOption.status === "" || item.status === filterOption.status;

    const quoteMatch =
      !activeQuoteId?.quoteId ||
      item.quote_id === activeQuoteId?.quoteId ||
      item.quote_id === null;

    return statusMatch && quoteMatch;
  });
}, [complaints, filterOption.status, activeQuoteId]);


  const handleEditClick = (complaint: any) => {
    setSelectedComplaint(complaint);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleAddClick = () => {
    setSelectedComplaint(null);
    setModalMode('add');
    setShowModal(true);
  };
  const tableHeadings: TableHeading[] = [
    { label: "Action", align: "left" },
    { label: "Service", align: "left" },
    { label: "Date", align: "left" },
    { label: "Reported Date", align: "left" },
    { label: "Title", align: "left" },
    { label: "Reported By", align: "left" },
    { label: "Insurance", align: "left" },
    { label: "Crew", align: "left" },
    { label: "Handler", align: "left" },
    { label: "Costs", align: "left" },
    { label: "Recovered", align: "left" },
    { label: "Status", align: "left" },
  ];
  return (
    <>
      <PageTitle
        title={"Complaints"}
        button={"Add"}
        onClick={handleAddClick}
      />
      <Box
        sx={{
          bgcolor: "#fff",
          height: { xs: "auto" },
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
          padding: "0px",
          overflow: "hidden",
        }}
      >
        <TableDesign
          tableHeadings={tableHeadings}
          tableHeadColor={"#02906f"}
          cellColor={"#fff"}
          pagination={filteredComplaints?.length > 0 ? null : null}
          isLoader={false}
          stickyHeader={true}
          filterOption={filterOption}
          setFilterOption={setFilterOption}
          //   extraCSS={{
          //     height: "calc(100%)",
          //   }}
        >
          {filteredComplaints?.length > 0 ? (
            filteredComplaints?.map((val: any, idx: number) => (
              <TableRow
                key={val?.complaint_id}
                sx={{
                  cursor: "pointer",
                  marginTop: "10px",
                  padding: "4px !important",
                  borderRadius: "20px !important",
                  height: "20px !important",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    outline: "2px solid #f0f0f0",
                    borderRadius: "0px !important",
                    outlineOffset: "-2px !important",
                  },
                }}
              >
                <TableCell align="left">
                  <CustomButton
                    title={"Edit"}
                    variant="outlined"
                    onClick={() => handleEditClick(val)}
                  />
                </TableCell>
                <TableCell>{val?.job_name}</TableCell>
                <TableCell>
                  {val.job_date
                    ? dayjs(val.job_date).format(
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY",
                      )
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    textWrap: "wrap",
                  }}
                >
                  {val.reported_date
                    ? dayjs(val.reported_date).format(
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY",
                      )
                    : ""}
                </TableCell>
                <TableCell>{val?.reported_by}</TableCell>
                <TableCell>{val?.title}</TableCell>
                <TableCell>{val?.insurance_type}</TableCell>
                <TableCell
                  width={"300px"}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {val?.crew
                    ?.map((crewMember: any) => crewMember.name)
                    .join(", ")}
                </TableCell>
                <TableCell>{val?.handled_by_name}</TableCell>
                <TableCell>{val?.costs}</TableCell>
                <TableCell>{val?.recovered}</TableCell>
                <TableCell>{val?.status}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={tableHeadings?.length} align="center">
                <Box
                  sx={{
                    height: "20vh",
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      fontSize: "1rem",
                      color: "#4A4A4A",
                      alignSelf: "center",
                    }}
                  >
                    {`No Data Found!`}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableDesign>
      </Box>

      {/* Complaint Detail Modal */}
      {showModal && (
        <ManageComplaint
          open={showModal}
          activeQuoteId={activeQuoteId}
          onHide={() => {
            setShowModal(false)
            setModalMode('add')
          }}
          title={modalMode === 'edit' ? 'Edit Complaint' : 'Add Complaint'}
          selectComplaint={modalMode === 'edit' ? selectedComplaint : null}
        />
      )}
    </>
  );
};

export default ComplaintList;
